/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Upgraded Halftone Node
   Hardware-accelerated screen-print rasterization supporting
   Circle, Square, and Line geometries with custom ink/paper palettes.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const HALFTONE_UPGRADED_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_frequency;
uniform float u_dotSize;
uniform float u_angle;
uniform int u_shape; // 0 = Circle, 1 = Square, 2 = Line
uniform float u_contrast;
uniform vec3 u_inkColor;
uniform vec3 u_paperColor;

void main() {
  vec4 src = texture(u_image, v_uv);
  
  // 1. Calculate luminance and apply contrast
  float lum = getLuminance(src.rgb);
  lum = clamp((lum - 0.5) * u_contrast + 0.5, 0.0, 1.0);
  
  // 2. Rotate coordinate space for screen print angle
  float rad = radians(u_angle);
  mat2 rot = mat2(cos(rad), -sin(rad), sin(rad), cos(rad));
  vec2 pixelPos = v_uv * u_resolution;
  vec2 rotatedPos = rot * pixelPos;
  
  // 3. Normalized cell coordinates [-0.5, 0.5]
  vec2 cell = fract(rotatedPos / max(u_frequency, 2.0)) - 0.5;
  
  // 4. Calculate distance metric based on shape
  float dist = 0.0;
  if (u_shape == 0) {
    // Circle
    dist = length(cell) * 2.0;
  } else if (u_shape == 1) {
    // Square
    dist = max(abs(cell.x), abs(cell.y)) * 2.0;
  } else if (u_shape == 2) {
    // Line / Stripe
    dist = abs(cell.y) * 2.0;
  }
  
  // 5. Target radius based on density and tone
  float targetRadius = (1.0 - lum) * u_dotSize;
  
  // 6. Anti-aliased edge calculation
  float edgeWidth = clamp(fwidth(dist) * 1.5, 0.02, 0.3);
  float dotMask = smoothstep(targetRadius + edgeWidth, targetRadius - edgeWidth, dist);
  
  // 7. Blend paper and ink colors
  vec3 finalColor = mix(u_paperColor, u_inkColor, dotMask);
  fragColor = vec4(finalColor, src.a);
}
`;

export const halftoneNode: NodeDefinition = {
  type: 'filter.halftone',
  category: 'Filter',
  label: 'Halftone Print',
  description: 'Screen-print rasterization with circle, square, and line dot patterns',
  keywords: ['halftone', 'print', 'raster', 'dots', 'screen', 'cmyk', 'newspaper', 'retro'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Halftone', type: 'image' },
  ],
  params: [
    {
      id: 'frequency',
      name: 'Grid Frequency',
      type: 'slider',
      defaultValue: 12,
      min: 4,
      max: 60,
      step: 1,
      unit: 'px',
    },
    {
      id: 'dotSize',
      name: 'Dot Density',
      type: 'slider',
      defaultValue: 1.0,
      min: 0.1,
      max: 2.5,
      step: 0.05,
    },
    {
      id: 'angle',
      name: 'Screen Angle',
      type: 'angle',
      defaultValue: 45,
      min: 0,
      max: 90,
      step: 1,
      unit: '°',
      uiHint: 'angle',
    },
    {
      id: 'shape',
      name: 'Pattern Shape',
      type: 'select',
      defaultValue: 'circle',
      options: [
        { label: 'Circle Dots', value: 'circle' },
        { label: 'Square Dots', value: 'square' },
        { label: 'Engraved Lines', value: 'line' },
      ],
    },
    {
      id: 'contrast',
      name: 'Print Contrast',
      type: 'slider',
      defaultValue: 1.2,
      min: 0.5,
      max: 3.0,
      step: 0.05,
    },
    {
      id: 'inkColor',
      name: 'Ink Color',
      type: 'color',
      defaultValue: '#111318',
    },
    {
      id: 'paperColor',
      name: 'Paper Color',
      type: 'color',
      defaultValue: '#f8f8f6',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: HALFTONE_UPGRADED_SHADER,
    getUniforms: (inputs, params, dimensions) => {
      const shapeStr = String(params.shape ?? 'circle');
      let shapeInt = 0;
      if (shapeStr === 'square') shapeInt = 1;
      else if (shapeStr === 'line') shapeInt = 2;

      // Parse hex colors into RGB vectors
      const parseHex = (hex: string, defaultRgb: [number, number, number]): [number, number, number] => {
        if (!hex || typeof hex !== 'string') return defaultRgb;
        const clean = hex.replace('#', '');
        if (clean.length === 6) {
          return [
            parseInt(clean.slice(0, 2), 16) / 255,
            parseInt(clean.slice(2, 4), 16) / 255,
            parseInt(clean.slice(4, 6), 16) / 255,
          ];
        }
        return defaultRgb;
      };

      const inkRgb = parseHex(String(params.inkColor ?? '#111318'), [0.07, 0.07, 0.1]);
      const paperRgb = parseHex(String(params.paperColor ?? '#f8f8f6'), [0.97, 0.97, 0.96]);

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_resolution: [dimensions.width, dimensions.height],
        u_frequency: Number(params.frequency) || 12,
        u_dotSize: Number(params.dotSize) || 1.0,
        u_angle: Number(params.angle) || 45,
        u_shape: shapeInt,
        u_contrast: Number(params.contrast) || 1.2,
        u_inkColor: inkRgb,
        u_paperColor: paperRgb,
      };
    },
  }),
};
